import type { MessageKey } from '@signbento/ui'

/**
 * Service-worker update handshake — S6.4 (PLT-10, §11.8).
 *
 * The rules, separated from the DOM so they can be tested: a version change is **announced**, never
 * applied on its own; a first install is not an update; the page keeps working (a draft is never
 * reloaded out from under the sender); and the banner stays until the person acts on it.
 */

export interface UpdateSignal {
  /** True when the page was already controlled by an older worker. */
  readonly had_controller: boolean
  /** The version the waiting worker reports, when it reports one. */
  readonly incoming_version: string | null
  /** The version this page was built with. */
  readonly current_version: string
}

export type UpdateDecision =
  | { readonly kind: 'IGNORE'; readonly reason: 'first_install' | 'same_version' }
  | { readonly kind: 'ANNOUNCE'; readonly message_key: MessageKey; readonly action_key: MessageKey }

/** A message key pair the banner uses; the product name never appears in the catalog. */
export const UPDATE_MESSAGES = {
  message_key: 'common.new_version_available',
  action_key: 'common.reload',
} as const

export function decideUpdate(signal: UpdateSignal): UpdateDecision {
  // The first install is not an update: the page was not controlled before it.
  if (!signal.had_controller) return { kind: 'IGNORE', reason: 'first_install' }
  if (signal.incoming_version !== null && signal.incoming_version === signal.current_version) {
    return { kind: 'IGNORE', reason: 'same_version' }
  }
  return { kind: 'ANNOUNCE', ...UPDATE_MESSAGES }
}

/** A reload is always the person's decision; nothing in the handshake may force one. */
export function mayReloadAutomatically(): boolean {
  return false
}

/**
 * Only a version message from this origin's own worker counts. A page that mixes contracts
 * across versions is exactly what this handshake exists to avoid.
 */
export function isVersionMessage(input: {
  readonly origin: string
  readonly expected_origin: string
  readonly data: unknown
}): input is {
  readonly origin: string
  readonly expected_origin: string
  readonly data: { readonly type: 'SW_VERSION'; readonly version: string }
} {
  if (input.origin !== input.expected_origin) return false
  const data = input.data as { type?: unknown; version?: unknown } | null
  return (
    typeof data === 'object' &&
    data !== null &&
    data.type === 'SW_VERSION' &&
    typeof data.version === 'string' &&
    data.version.length > 0
  )
}
